import { CartLinesUpdateEvent } from '@shopify/events';
import { sectionRenderer } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';

/**
 * Keeps Cowlendar bookings whole in the theme's cart.
 *
 * A booking is one cart line per participant category, all carrying the same `__cow_internal_id`
 * property. This module does two things with them:
 *
 * - Relays Cowlendar's add-to-cart. Cowlendar adds the booking with its own request and announces it
 *   with a `cart:update` CustomEvent on document whose `detail.cart` is the new cart. The cart drawer,
 *   the cart page and the cart icon listen only for the Standard Events `shopify:cart:lines-update`,
 *   and repaint from the section markup that event's promise resolves with, so the change is
 *   re-announced in that form and the drawer is opened. A `cart:update` whose cart holds no booking
 *   line is ignored.
 * - Removes a booking as a whole. snippets/cart-products-mab.liquid gives a booking line's remove
 *   button `data-cow-booking` instead of the native remove handler; a click sets every line of that
 *   booking to zero in one request, with the cart items disabled until it settles.
 *
 * Supplements added through Cowlendar's upsell carry no `__cow_internal_id`, so they are ordinary
 * lines here: editable, and left in the cart when their booking is removed.
 *
 * Loaded by snippets/cart-products-mab.liquid, so it runs wherever the cart renders.
 */

const SOURCE = 'cowlendar-cart';

/**
 * The booking a cart line belongs to, or an empty string for an ordinary line.
 * @param {import('@shopify/events').CartAjaxResponse['items'][number]} item
 * @returns {string}
 */
function bookingOf(item) {
  const properties = /** @type {Record<string, string>} */ (item.properties ?? {});
  return properties.__cow_internal_id ?? '';
}

/**
 * Section ids of every cart-items component on the page.
 * @returns {string[]}
 */
function cartSectionIds() {
  const ids = new Set();
  for (const item of document.querySelectorAll('cart-items-component')) {
    if (item instanceof HTMLElement && item.dataset.sectionId) ids.add(item.dataset.sectionId);
  }
  return Array.from(ids);
}

/**
 * The cart sections rendered for the current page, keyed by section id.
 * @param {string[]} ids
 * @returns {Promise<Record<string, string>>}
 */
async function renderCartSections(ids) {
  if (!ids.length) return {};
  const response = await fetch(`${window.location.pathname}?sections=${ids.join(',')}`);
  if (!response.ok) throw new Error(`Cart sections unavailable: HTTP ${response.status}`);
  return response.json();
}

/**
 * Resolves an announced cart change with the given cart and sections.
 * @param {ReturnType<typeof import('@shopify/events').CartLinesUpdateEvent.createPromise>} deferred
 * @param {import('@shopify/events').CartAjaxResponse} cart
 * @param {Promise<Record<string, string>>} sectionsPromise
 */
async function resolveCartChange(deferred, cart, sectionsPromise) {
  try {
    const sections = await sectionsPromise;
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: SOURCE,
        itemCount: cart.item_count,
        sections,
        didError: false,
      },
    });
  } catch (error) {
    deferred.reject(error);
    throw error;
  }
}

/**
 * Opens the cart drawer, whatever the theme's open-on-add setting. A drawer that opens on add by
 * itself, or is already open, is left alone.
 */
function openCartDrawer() {
  const drawerComponent = document.querySelector('cart-drawer-component');
  if (!drawerComponent || drawerComponent.hasAttribute('auto-open')) return;
  const drawer = /** @type {(HTMLElement & { isOpen?: boolean, open?: () => void }) | null} */ (
    drawerComponent.closest('theme-drawer')
  );
  if (drawer && !drawer.isOpen) drawer.open?.();
}

/**
 * @param {Event} event
 */
async function relayCowlendarAdd(event) {
  /** @type {import('@shopify/events').CartAjaxResponse | undefined} */
  const cart = event instanceof CustomEvent ? event.detail?.cart : undefined;
  if (!cart || !Array.isArray(cart.items) || !cart.item_count) return;
  const bookingLines = cart.items.filter((item) => bookingOf(item));
  if (!bookingLines.length) return;

  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: bookingLines.map((item) => ({ merchandiseId: String(item.variant_id), quantity: item.quantity })),
      promise: deferred.promise,
    })
  );

  try {
    await resolveCartChange(deferred, cart, renderCartSections(cartSectionIds()));
    openCartDrawer();
  } catch (error) {
    console.warn('[cowlendar-cart] Relay failed:', error);
  }
}

/**
 * @param {MouseEvent} event
 */
async function removeBooking(event) {
  const button = event.target instanceof Element ? event.target.closest('[data-cow-booking]') : null;
  if (!(button instanceof HTMLButtonElement) || button.disabled) return;

  event.preventDefault();
  const booking = button.dataset.cowBooking;
  const component = button.closest('cart-items-component');
  button.disabled = true;
  component?.classList.add('cart-items-disabled');

  try {
    /** @type {import('@shopify/events').CartAjaxResponse} */
    const current = await (await fetch(`${Theme.routes.cart_url}.js`)).json();
    /** @type {Record<string, number>} */
    const updates = {};
    for (const item of current.items) {
      if (bookingOf(item) === booking) updates[item.key] = 0;
    }

    const ids = cartSectionIds();
    if (!Object.keys(updates).length) {
      await Promise.all(ids.map((id) => sectionRenderer.renderSection(id, { cache: false })));
      return;
    }

    const response = await fetch(Theme.routes.cart_update_url, {
      ...fetchConfig('json', {
        body: JSON.stringify({ updates, sections: ids.join(','), sections_url: window.location.pathname }),
      }),
    });
    const cart = await response.json();
    if (!response.ok || cart.status) throw new Error(cart.description || cart.message || `HTTP ${response.status}`);

    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'remove',
        context: 'cart',
        lines: Object.keys(updates).map((key) => ({ id: key, quantity: 0 })),
        promise: deferred.promise,
      })
    );
    await resolveCartChange(deferred, cart, Promise.resolve(cart.sections ?? {}));
  } catch (error) {
    console.warn('[cowlendar-cart] Booking removal failed:', error);
  } finally {
    button.disabled = false;
    component?.classList.remove('cart-items-disabled');
  }
}

document.addEventListener('cart:update', relayCowlendarAdd);
document.addEventListener('click', removeBooking);
